import { Chip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";

import { AvailabilityForm, OnBookingSubmitProps } from "../..";
import { Availability, OnBookingCompletedCallback } from "../../../apiHooks";
import { ProductQuery } from "../../../apiHooks/graphql";
import { useNextTranslation, useProductPromotionConfig } from "../../../hooks";
import { ProductBookNowButtonProps } from "../../../product/ProductBookNowButton";
import { BookingConsumerTripInput } from "../../../types/ProductFullLoaded";
import { InternalAvailabilityModalContentHeader } from "./InternalAvailabilityModalContentHeader";

const AvailabilitySelectWrapper = styled("div")({
    display: "flex",
    boxSizing: "border-box",
    flexDirection: "column",
    width: "100%",
});

const StyledHr = styled("hr")(({ theme }) => ({
    color: "inherit",
    border: "0",
    margin: "1rem 0",
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    fontSize: "14px",
    color: theme.palette.common.white,
}));

const FreeCancellationHeader = styled(Typography)(({ theme }) => ({
    "&": {
        fontSize: 16,
        fontWeight: "bold",
        color: theme.palette.success.main,
    },
}));

const FreeCancellationWrapper = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const FreeCancellationText = styled(Typography)(({ theme }) => ({
    "&": {
        color: theme.palette.text.secondary,
        fontSize: 14,
    },
}));

type InternalAvailabilityModalContentProps = {
    product: ProductQuery["product"];
    bookingReference?: string;
    consumerTrip?: BookingConsumerTripInput;
    onBookingCompleted?: OnBookingCompletedCallback;
    onAvailabilityChange?: (availability?: string | null) => void;
    BookingButtonComponent?: (props: ProductBookNowButtonProps) => JSX.Element;
    submitButtonLabel?: string;
    onBookingSubmit?: (props: OnBookingSubmitProps) => Promise<void>;
    saleCurrency?: string;
    initialAvailability?: Availability;
};

export const InternalAvailabilityModalContent = (props: InternalAvailabilityModalContentProps) => {
    const {
        product,
        bookingReference,
        consumerTrip,
        onBookingCompleted,
        onAvailabilityChange,
        BookingButtonComponent,
        submitButtonLabel,
        onBookingSubmit,
        saleCurrency,
    } = props;
    // TODO: Don't try and set options based on previous availability for sources that use the new availability option pattern
    // In future we should validate that an availability with the provided optons is not sold out via the GraphQL API
    let initialAvailability = props.initialAvailability;
    if (["SEVEN_ROOMS", "LIGHTSPEED"].includes(product.sourceInstance!.sourceName!)) {
        initialAvailability = undefined;
    }
    const [t] = useNextTranslation("availability");
    const [availabilityListItemId, setAvailabilityListItemId] = useState<string | null>(null);

    const onAvailabilityChangeHandler = (availabilityId: string | null | undefined = null) => {
        setAvailabilityListItemId(availabilityId);
        if (onAvailabilityChange) {
            onAvailabilityChange(availabilityId);
        }
    };

    const promotionConfig = useProductPromotionConfig({
        promotionType: product.promotionType ?? undefined,
        isFeatured: product.isFeatured ?? undefined,
    });

    const shouldAutoFillQuestions = product.type !== "HOTEL";

    return (
        <AvailabilitySelectWrapper>
            {promotionConfig && product.isFeatured && product.promotionType && (
                <div>
                    <StyledChip style={{ backgroundColor: promotionConfig.color }} label={promotionConfig.label} />
                    <StyledHr />
                </div>
            )}

            <InternalAvailabilityModalContentHeader
                product={product}
                availabilityListItemId={availabilityListItemId}
                initialAvailability={initialAvailability}
                onAvailabilityChange={onAvailabilityChangeHandler}
            />

            {availabilityListItemId && (
                <>
                    <br />
                    <AvailabilityForm
                        key={availabilityListItemId}
                        availabilityId={availabilityListItemId}
                        onBookingCompleted={onBookingCompleted}
                        bookingReference={bookingReference}
                        consumerTrip={consumerTrip}
                        BookingButtonComponent={BookingButtonComponent}
                        shouldAutoFillQuestions={shouldAutoFillQuestions}
                        submitButtonLabel={submitButtonLabel}
                        onBookingSubmit={onBookingSubmit}
                        saleCurrency={saleCurrency}
                        initialAvailability={initialAvailability}
                    />
                </>
            )}

            {product.cancellationPolicy.hasFreeCancellation && (
                <FreeCancellationWrapper>
                    <FreeCancellationHeader>{t("message.freeCancellation")}</FreeCancellationHeader>
                    {product.cancellationPolicy.penaltyList.nodes[0] && (
                        <FreeCancellationText>
                            {product.cancellationPolicy.penaltyList.nodes[0].formattedText}
                        </FreeCancellationText>
                    )}
                </FreeCancellationWrapper>
            )}
        </AvailabilitySelectWrapper>
    );
};
